import { formatTime, type CellValue, type ColumnDef, type Rational } from "@helm/core";
import {
  MINUS,
  formatAvgPrice,
  formatCents,
  formatPerContractTicks,
  formatTicks,
  pnlTone,
  type PriceFormat,
} from "@helm/ui";

/**
 * Cell formatting for the column registries (`@helm/core` tables): one place that turns a
 * column's kind + value into text and a tone class. Money stays integer cents until here.
 */

export type CellCtx<R> = {
  privacy: boolean;
  /** IANA zone for time columns. */
  zone: string;
  /** The row's instrument price format (null = not loaded: raw ticks are shown). */
  priceFormat: (row: R) => PriceFormat | null;
  /** Changes when any instrument's format loads, so memoized rows re-read `priceFormat`. */
  formats?: number;
};

export type CellOut = { text: string; className: string; title?: string };

const isRational = (v: CellValue): v is Rational => typeof v === "object" && v !== null;
const bigOf = (v: CellValue): bigint | null =>
  typeof v === "bigint" ? v : typeof v === "number" && Number.isSafeInteger(v) ? BigInt(v) : null;

const toneOf = (v: bigint | null): string => `tone-${pnlTone(v === null ? null : Number(v))}`;

/** "1:05:09" / "4:07" (h:mm:ss, m:ss under an hour). */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${String(h)}:${String(m).padStart(2, "0")}:${ss}` : `${String(m)}:${ss}`;
}

const NUMERIC_KINDS: ReadonlySet<string> = new Set([
  "qty",
  "signedQty",
  "int",
  "money",
  "pnl",
  "price",
  "avgPrice",
  "ticksPerContract",
  "time",
  "duration",
  "tickValue",
]);

/** Right-aligned, tabular (SPEC §17.4). */
export function isNumericColumn<R>(col: ColumnDef<R>): boolean {
  return NUMERIC_KINDS.has(col.kind);
}

/** The tooltip of an unknown value in a column whose feed does not exist yet. */
export function pendingTitle<R>(col: ColumnDef<R>): string | undefined {
  return col.pending === undefined ? undefined : `Not available yet — ${col.pending}`;
}

/**
 * A cell's text, classes and tooltip. Columns with a per-row tooltip (`rowTitle`) say where a
 * value came from or which setting is missing; `level` adds the A3 warning colour (75 % / 90 % of
 * a limit consumed); `dash` shows an unknown amount as "—".
 */
export function formatCell<R>(col: ColumnDef<R>, row: R, ctx: CellCtx<R>): CellOut {
  const out = baseCell(col, row, ctx);
  const title = col.rowTitle?.(row) ?? null;
  const level = col.level?.(row) ?? null;
  const badge = col.badge?.(row) ?? null;
  const classes = [
    out.className,
    level !== null ? `level-${level}` : "",
    badge !== null ? `badge-${badge}` : "",
  ]
    .filter((c) => c !== "")
    .join(" ");
  return {
    ...out,
    ...(title !== null ? { title } : {}),
    ...(level !== null || badge !== null ? { className: classes } : {}),
  };
}

function baseCell<R>(col: ColumnDef<R>, row: R, ctx: CellCtx<R>): CellOut {
  const v = col.value(row);
  const num = isNumericColumn(col) ? "num" : "";
  const unknown = (text: string): CellOut => {
    const title = pendingTitle(col);
    const t = col.dash === true ? "—" : text;
    return title === undefined ? { text: t, className: num } : { text: t, className: num, title };
  };
  switch (col.kind) {
    case "money": {
      const c = bigOf(v);
      return c === null
        ? unknown("$ --")
        : { text: formatCents(c, { privacy: ctx.privacy }), className: num };
    }
    case "pnl": {
      const c = bigOf(v);
      if (c === null) return unknown("$ --");
      return {
        text: formatCents(c, { privacy: ctx.privacy, signed: true }),
        className: `${num} ${toneOf(c)}`,
      };
    }
    case "tickValue": {
      const c = bigOf(v);
      return c === null ? unknown("—") : { text: formatCents(c), className: num };
    }
    case "price": {
      if (typeof v !== "number" && typeof v !== "bigint") return unknown("—");
      const f = ctx.priceFormat(row);
      return { text: f ? formatTicks(Number(v), f) : `${String(v)} t`, className: num };
    }
    case "avgPrice": {
      if (!isRational(v)) return unknown("—");
      return { text: formatAvgPrice(v, ctx.priceFormat(row)), className: num };
    }
    case "ticksPerContract": {
      if (!isRational(v) || v.den === 0n) return unknown("—");
      return { text: formatPerContractTicks(v.num, Number(v.den)), className: `${num} ${toneOf(v.num)}` };
    }
    case "signedQty": {
      if (typeof v !== "number") return unknown("—");
      const text = v > 0 ? `+${String(v)}` : v < 0 ? `${MINUS}${String(-v)}` : "0";
      return { text, className: `${num} ${v > 0 ? "tone-pos" : v < 0 ? "tone-neg" : "tone-zero"}` };
    }
    case "qty":
    case "int": {
      const n = typeof v === "number" || typeof v === "bigint" ? v : null;
      return n === null ? unknown("—") : { text: n.toLocaleString("en-US"), className: num };
    }
    case "time":
      return typeof v === "number" ? { text: formatTime(v, ctx.zone), className: num } : unknown("—");
    case "duration":
      return typeof v === "number" ? { text: formatDuration(v), className: num } : unknown("—");
    case "account":
      return typeof v === "string" ? { text: ctx.privacy ? "***" : v, className: "" } : unknown("—");
    case "side":
      if (v === "Buy" || v === "Long") return { text: v, className: "tone-pos" };
      if (v === "Sell" || v === "Short") return { text: v, className: "tone-neg" };
      return unknown("—");
    case "action":
      return { text: "", className: "" };
    case "text":
    case "symbol":
    case "status":
      return typeof v === "string" ? { text: v, className: "" } : unknown("—");
  }
}
